// ── Login view ─────────────────────────────────────────────────────────────────
// Login rendered as a phase of the main modal — the same card that morphs
// between upload → login → avatars, so it reads as one shared element.

import type { TryStyleClient } from '../../client';
import type { FirebaseConfig, ViewContext } from '../modal-types';
import { esc } from '../helpers';
import { pwLogoHTML, longLogoHTML } from '../logos';
import { GOOGLE_SVG, APPLE_SVG } from '../icons';

const CDN = 'https://www.gstatic.com/firebasejs/10.12.0';

function loadScript(src: string): Promise<void> {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload  = () => res();
    s.onerror = () => rej(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function getFirebaseAuth(cfg: FirebaseConfig): Promise<any> {
  const w = window as any;
  if (!w.firebase?.auth) {
    await loadScript(`${CDN}/firebase-app-compat.js`);
    await loadScript(`${CDN}/firebase-auth-compat.js`);
  }
  if (!w.firebase.apps.length) {
    w.firebase.initializeApp({
      apiKey: cfg.apiKey,
      authDomain: cfg.authDomain,
      projectId: cfg.projectId,
    });
  }
  return w.firebase.auth();
}

export function renderLogin(ctx: ViewContext): void {
  const { state, content, opts, client } = ctx;
  const fb = opts.firebaseConfig;

  const poweredRow = opts.showBranding
    ? `<div class="ts-powered-row">${pwLogoHTML(16)}</div>`
    : '';

  content.innerHTML = `
    ${ctx.headerHTML()}
    <div class="ts-mbody">
      <div class="ts-login-hdr">
        ${poweredRow}
        <h2 class="ts-login-title">Sign in to <span class="ts-login-logo">${longLogoHTML(22)}</span></h2>
      </div>
      <div class="ts-error" id="ts-ldlg-err" style="display:none"></div>
      ${fb ? `<div class="ts-login-social">
        <button class="ts-social-btn" id="ts-ldlg-google" type="button" aria-label="Continue with Google">${GOOGLE_SVG}</button>
        <button class="ts-social-btn" id="ts-ldlg-apple" type="button" aria-label="Continue with Apple">${APPLE_SVG}</button>
      </div>` : ''}
      ${fb ? `<div class="ts-or-divider"><div class="ts-or-line"></div><span>or</span><div class="ts-or-line"></div></div>` : ''}
      <input class="ts-inp" type="text" id="ts-ldlg-email" placeholder="Username / Email" autocomplete="username" />
      <input class="ts-inp" type="password" id="ts-ldlg-pw" placeholder="Password" autocomplete="current-password" />
      <button class="ts-btn-primary" id="ts-ldlg-submit" type="button" disabled>Sign In</button>
    </div>
  `;

  const showErr = (msg: string): void => {
    const e = content.querySelector<HTMLElement>('#ts-ldlg-err')!;
    e.textContent = msg; e.style.display = '';
  };
  const hideErr = (): void => {
    content.querySelector<HTMLElement>('#ts-ldlg-err')!.style.display = 'none';
  };

  const emailInp  = content.querySelector<HTMLInputElement>('#ts-ldlg-email')!;
  const pwInp     = content.querySelector<HTMLInputElement>('#ts-ldlg-pw')!;
  const submitBtn = content.querySelector<HTMLButtonElement>('#ts-ldlg-submit')!;

  const syncBtn = (): void => { submitBtn.disabled = !emailInp.value.trim() || !pwInp.value; };
  emailInp.addEventListener('input', syncBtn);
  pwInp.addEventListener('input', syncBtn);

  submitBtn.addEventListener('click', async () => {
    const email = emailInp.value.trim();
    const pw    = pwInp.value;
    if (!email || !pw) return;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Signing in…';
    hideErr();
    try {
      await client.auth.login(email, pw);
      void ctx.handleLoginSuccess();
    } catch (e: any) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign In';
      showErr(e?.message ?? 'Sign-in failed.');
    }
  });

  if (!fb) return;

  async function socialLogin(popup: () => Promise<any>): Promise<void> {
    hideErr();
    try {
      const result  = await popup();
      const idToken = await result.user.getIdToken(false);
      await client.auth.loginWithFirebase(idToken);
      void ctx.handleLoginSuccess();
    } catch (e: any) {
      showErr(e?.message ?? 'Sign-in failed.');
    }
  }

  content.querySelector('#ts-ldlg-google')?.addEventListener('click', async () => {
    const auth = await getFirebaseAuth(fb);
    const w    = window as any;
    const prov = new w.firebase.auth.GoogleAuthProvider();
    prov.addScope('email');
    prov.addScope('profile');
    void socialLogin(() => auth.signInWithPopup(prov));
  });

  content.querySelector('#ts-ldlg-apple')?.addEventListener('click', async () => {
    const auth = await getFirebaseAuth(fb);
    const w    = window as any;
    const prov = new w.firebase.auth.OAuthProvider('apple.com');
    prov.addScope('email');
    prov.addScope('name');
    void socialLogin(() => auth.signInWithPopup(prov));
  });
}